import { joiResolver } from '@hookform/resolvers/joi';
import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react';
import { DefaultValues, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { useHistory } from 'react-router-dom';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { useGetDocumentUploadStatus } from '../../../api-refactored/document/useGetDocumentUploadStatus';
import { ResourceResponse } from '../../../api-refactored/onlineTutorials/useGetResource';
import { usePostAddResource } from '../../../api-refactored/onlineTutorials/usePostAddResource';
import { usePostUploadTutorialsDocument } from '../../../api-refactored/onlineTutorials/usePostUploadTutorialsDocument';
import { usePutEditResource } from '../../../api-refactored/onlineTutorials/usePutEditResource';
import { usePutUploadToS3 } from '../../../api-refactored/s3-upload/usePutUploadFileToS3';
import { KeyValueResponse } from '../../../api-refactored/typings';
import { ResourceFormData } from '../../../models';
import { FileUploadState } from '../../../models/File';
import { useOnlineTutorialsContext } from '../../../routes/AdminRoute';
import { prepareFiles } from '../../../utils/UploadUtil';
import resourceSchema from '../schema/resourceSchema';

const getResourceTypePayload = (fileExtension: string) => {
  let resourceType = 'PDF';
  if (fileExtension === 'MP4') {
    resourceType = 'VIDEO';
  }
  return resourceType;
};

interface ResourceFormReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  /**
   * Form to send edit & delete
   */
  formHook: UseFormReturn<ResourceFormData>;
  draftFiles: UploadedFile[];
  setDraftFiles: Dispatch<SetStateAction<UploadedFile[]>>;
  fileErrorMessage: string;
  setFileErrorMessage: Dispatch<SetStateAction<string>>;
  onHandleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  files: UploadedFile[];
  setFiles: Dispatch<SetStateAction<UploadedFile[]>>;
  apiButtonStatus: MutationStatus;
  /**
   * React query mutation object
   */
}

/**
 * A hook to handle search functionality
 * @returns
 */

interface UseResourceFormProps {
  defaultFormValues?: DefaultValues<ResourceFormData>;
  resourceData: ResourceResponse | undefined;
  resourceTypeData: KeyValueResponse[] | undefined;
  isEditMode: boolean;
}

export const useResourceForm = ({
  defaultFormValues,
  resourceData,
  isEditMode,
  resourceTypeData,
}: UseResourceFormProps): ResourceFormReturn => {
  const history = useHistory();
  const { t } = useTranslation();
  const { setIsAddSuccess, setIsEditSuccess } = useOnlineTutorialsContext();

  const formHook = useForm<ResourceFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(resourceSchema),
    defaultValues: defaultFormValues,
  });

  const { getValues, handleSubmit } = formHook;

  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [isS3Success, setIsS3Success] = useState<boolean>(false);
  const [fileKey, setFileKey] = useState<string>(resourceData?.fileKey || '');
  const [fileErrorMessage, setFileErrorMessage] = useState<string>('');
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  useEffect(() => {
    if (apiButtonStatus === 'error') {
      setTimeout(() => {
        setApiButtonStatus('idle');
      }, 200);
    }
  }, [apiButtonStatus]);

  const { mutate: editResourceMutate } = usePutEditResource({
    uuid: resourceData?.uuid || '',
    onSuccess: () => {
      setFileKey('');
      setIsS3Success(false);
      setApiButtonStatus('success');
      setIsEditSuccess(true);
      history.goBack();
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  const { mutate: addResourceMutate } = usePostAddResource({
    onSuccess: () => {
      setFileKey('');
      setIsS3Success(false);
      setApiButtonStatus('success');
      setIsAddSuccess(true);
      history.goBack();
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  const onHandleEditResource = () => {
    const { description, examLevel, title } = getValues();

    const resourceTypeCode = resourceTypeData?.find(
      (v) => v.description === resourceData?.resourceType
    )?.code;

    editResourceMutate({
      fileKey: resourceData?.fileKey || '',
      examLevel: examLevel.code,
      description,
      title,
      fileName: resourceData?.fileName || '',
      resourceType: resourceTypeCode || '',
    });
  };

  const { mutate: uploadMutate } = usePutUploadToS3({
    onSuccess: () => {
      setIsS3Success(true);
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  const { mutate, status: uploadTutorialsDocumentStatus } = usePostUploadTutorialsDocument({
    onSuccess: (res) => {
      const { filenames } = prepareFiles(files, setFiles);

      const fileExtension = filenames[0]
        .slice(filenames[0].lastIndexOf('.') + 1)
        .toLocaleUpperCase();
      const contentType = fileExtension === 'PDF' ? 'application/pdf' : 'video/mp4';
      uploadMutate({
        data: files[0].file,
        putUrl: res.presignedUrl,
        customHeader: {
          headers: { 'Content-Type': contentType },
        },
      });
      setFileKey(res.key);
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  useGetDocumentUploadStatus({
    fileKey,
    enabled: uploadTutorialsDocumentStatus === 'success' && !!fileKey && isS3Success,
    refetchInterval: 10000,
    onSuccess: (response) => {
      const { description, examLevel, title } = getValues();
      const { filenames } = prepareFiles(files, setFiles);

      const fileExtension = filenames[0]
        .slice(filenames[0].lastIndexOf('.') + 1)
        .toLocaleUpperCase();

      if (response.fileStatus === FileUploadState.SUCCESS) {
        if (isEditMode) {
          editResourceMutate({
            fileKey,
            examLevel: examLevel.code,
            description,
            title,
            fileName: filenames[0],
            resourceType: getResourceTypePayload(fileExtension),
          });
        } else {
          addResourceMutate({
            description,
            fileKey,
            examLevel: examLevel.code,
            title,
            fileName: filenames[0],
            resourceType: getResourceTypePayload(fileExtension),
          });
        }
      }
      if (response.fileStatus === FileUploadState.FAILED) {
        setApiButtonStatus('error');
      }
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  const onHandleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isNoValidateFile = !isEditMode && !files.length;
    if (isNoValidateFile) {
      setFileErrorMessage(t('usefulInformation->>ResourceForm.FileValidationErrMess'));
    }
    handleSubmit(() => {
      if (isNoValidateFile) {
        return;
      }
      setApiButtonStatus('loading');
      if (isEditMode && !files.length) {
        onHandleEditResource();
      } else {
        const { filenames } = prepareFiles(files, setFiles);
        mutate(filenames[0]);
      }
    })();
  };

  return {
    formHook,
    onHandleSubmit,
    draftFiles,
    setDraftFiles,
    fileErrorMessage,
    setFileErrorMessage,
    files,
    setFiles,
    apiButtonStatus,
  };
};
